/**
 * Creation Wizard
 * Step-by-step wizard for reviewing, testing, and deploying agents
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  Circle,
  ArrowLeft,
  ArrowRight,
  Rocket,
  TestTube,
  Eye,
  Settings,
  Loader2
} from 'lucide-react';
import FlowDiagram from './FlowDiagram';
import TestingSandbox from './testing/TestingSandbox';

interface CreationWizardProps {
  agentDefinition: any;
  conversationId: string;
  onBack: () => void;
}

const STEPS = [
  { id: 'review', label: 'Review Requirements', icon: Eye },
  { id: 'workflow', label: 'Workflow Design', icon: Settings },
  { id: 'test', label: 'Test & Validate', icon: TestTube },
  { id: 'deploy', label: 'Deploy Agent', icon: Rocket }
];

export default function CreationWizard({
  agentDefinition,
  conversationId,
  onBack
}: CreationWizardProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [testResults, setTestResults] = useState<any>(null);
  const [deploying, setDeploying] = useState(false);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      // TODO: Call deploy API
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate deployment
      navigate(`/agents/hub`);
    } catch (error) {
      console.error('Deployment failed:', error);
    } finally {
      setDeploying(false);
    }
  };

  const renderStepContent = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 'review':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Agent Overview</h3>
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Agent Name</p>
                  <p className="font-semibold text-lg">{agentDefinition.name}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p>{agentDefinition.description}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Type</p>
                    <Badge variant={agentDefinition.agent_type === 'internal' ? 'default' : 'secondary'}>
                      {agentDefinition.agent_type}
                    </Badge>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Domain</p>
                    <p className="font-medium">{agentDefinition.domain}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Execution</p>
                    <Badge variant="outline">Volcano SDK</Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Capabilities</p>
                  <div className="flex flex-wrap gap-2">
                    {agentDefinition.capabilities?.map((cap: string, index: number) => (
                      <Badge key={index} variant="outline">
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Workflow Summary</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Workflow Steps</p>
                    <p className="text-2xl font-bold">
                      {agentDefinition.volcano_workflow?.steps.length || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Estimated Cost</p>
                    <p className="text-2xl font-bold text-green-600">
                      {agentDefinition.volcano_workflow?.metadata?.estimated_cost || '$0.001'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'workflow':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Workflow Visualization</h3>
              <p className="text-sm text-gray-600 mb-4">
                Review the multi-step workflow designed by Agent Custodian
              </p>
            </div>
            
            <FlowDiagram
              workflow={agentDefinition.volcano_workflow}
              agentName={agentDefinition.name}
            />
          </div>
        );

      case 'test':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Test Your Agent</h3>
              <p className="text-sm text-gray-600 mb-4">
                Run tests in a safe sandbox environment with simulated data
              </p>
            </div>

            <TestingSandbox
              agentId={agentDefinition.id}
              agentName={agentDefinition.name}
              onTestComplete={(results) => setTestResults(results)}
            />
          </div>
        );

      case 'deploy':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Ready to Deploy</h3>
              <p className="text-sm text-gray-600">
                Your agent is ready to be deployed to the draft stage
              </p>
            </div>

            {testResults && (
              <Alert
                variant={testResults.passed ? 'default' : 'destructive'}
                className={testResults.passed ? 'border-green-500 bg-green-50' : ''}
              >
                <AlertDescription>
                  {testResults.passed ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-900">Tests Passed!</p>
                        <p className="text-sm text-green-700 mt-1">
                          Success rate: {testResults.stats?.success_rate.toFixed(1)}% | 
                          Avg latency: {testResults.stats?.avg_latency_ms.toFixed(0)}ms |
                          Avg cost: ${testResults.stats?.avg_cost_usd.toFixed(4)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="font-semibold">Some tests failed</p>
                      <p className="text-sm mt-1">
                        Review the test results and make adjustments if needed
                      </p>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold mb-4">Deployment Options</h4>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="deployment"
                    id="draft"
                    defaultChecked
                    className="mt-1"
                  />
                  <label htmlFor="draft">
                    <p className="font-medium">Draft Stage</p>
                    <p className="text-sm text-gray-600">
                      Deploy to draft for internal testing and review
                    </p>
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="deployment"
                    id="staging"
                    disabled={!testResults?.passed}
                    className="mt-1"
                  />
                  <label htmlFor="staging" className={!testResults?.passed ? 'opacity-50' : ''}>
                    <p className="font-medium">Staging Stage</p>
                    <p className="text-sm text-gray-600">
                      Deploy to staging for pre-production testing (requires passing tests)
                    </p>
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="deployment"
                    id="production"
                    disabled
                    className="mt-1"
                  />
                  <label htmlFor="production" className="opacity-50">
                    <p className="font-medium">Production Stage</p>
                    <p className="text-sm text-gray-600">
                      Requires manual promotion after successful staging deployment
                    </p>
                  </label>
                </div>
              </div>
            </div>

            <Button
              onClick={handleDeploy}
              disabled={deploying}
              className="w-full h-12 text-lg"
            >
              {deploying ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5 mr-2" />
                  Deploy Agent
                </>
              )}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Creation Wizard</h1>
            <p className="text-sm text-gray-600 mt-1">{agentDefinition.name}</p>
          </div>
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Conversation
          </Button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-100 text-green-600'
                        : isActive
                        ? 'bg-indigo-100 text-indigo-600'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.label}
                    </p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${isCompleted ? 'bg-green-200' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              {renderStepContent()}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t px-6 py-4">
        <div className="max-w-5xl mx-auto flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep < STEPS.length - 1 && (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

